import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  AuthResponse, User, Hackathon, Event, ApiResponse, PaginatedResponse, Quiz, QuizResult, Project, RegistrationDetails, TeamFilters, CreateTeamData, Team,
  TeamDetail,
  SentJoinRequest,
  MyTeam,
  UserProfile,
  UserFilters,
  OAuthUrlResponse, AuthResponsed
} from '../types';

axios.defaults.withCredentials = true;

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Enable cookies
    });

    // this.api.interceptors.request.use((config) => {
    //   config.headers['x-platform'] = 'web';
    //   return config;
    // });

    // Remove the request interceptor that sets Authorization header
    // Only keep the response interceptor for auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async signup(userData: {
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<ApiResponse<User>> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.post('/api/auth/signup', userData);
    return response.data;
  }

  async login(credentials: {
    identifier: string;
    password: string;
  }): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/api/auth/login', credentials);
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post('/api/auth/logout', {}, {
      withCredentials: true // Important for cookie handling in production
    });
    // No need to clear localStorage or cookies
  }

  // ─── Subscription endpoints ────────────────────────────────────────────────
  async getSubscriptionPlans(): Promise<any> {
    const response = await this.api.get('/api/subscription/plans');
    return response.data;
  }

  async getSubscriptionStatus(): Promise<any> {
    const response = await this.api.get('/api/subscription/status');
    return response.data;
  }

  async validateReferralCode(code: string): Promise<any> {
    const response = await this.api.get(`/api/subscription/validate-referral/${code}`);
    return response.data;
  }

  async createSubscription(planKey: string, referralCode?: string, isAutoPay: boolean = true): Promise<any> {
    const response = await this.api.post('/api/subscription/create', { planKey, referralCode, isAutoPay });
    return response.data;
  }


  async verifySubscription(data: {
    razorpay_payment_id: string;
    razorpay_subscription_id: string;
    razorpay_signature: string;
  }): Promise<any> {
    const response = await this.api.post('/api/subscription/verify', data);
    return response.data;
  }

  async verifyOrder(data: {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
  }): Promise<any> {
    const response = await this.api.post('/api/subscription/verify-order', data);
    return response.data;
  }

  async cancelSubscription(): Promise<any> {
    const response = await this.api.post('/api/subscription/cancel');
    return response.data;
  }


  // OAuth URL endpoints with proper error handling
  async getGoogleOAuthUrl(): Promise<{ url: string }> {
    try {
      const response: AxiosResponse<OAuthUrlResponse> = await this.api.get(
        '/api/auth/google/url',
        { withCredentials: true }
      );

      console.log('Google OAuth API response:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to get Google OAuth URL');
      }

      // Handle both possible response formats
      const authUrl = response.data.url || response.data.authUrl;

      if (!authUrl) {
        throw new Error('No OAuth URL returned from server');
      }

      return { url: authUrl };

    } catch (error: any) {
      console.error('Google OAuth URL error:', error);

      // Re-throw with more context
      if (error.response) {
        throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        throw new Error('Network error - could not reach authentication server');
      } else {
        throw error;
      }
    }
  }

  async getGitHubOAuthUrl(): Promise<{ url: string }> {
    try {
      const response: AxiosResponse<OAuthUrlResponse> = await this.api.get(
        '/api/auth/github/url',
        { withCredentials: true }
      );

      console.log('GitHub OAuth API response:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to get GitHub OAuth URL');
      }

      // Handle both possible response formats
      const authUrl = response.data.url || response.data.authUrl;

      if (!authUrl) {
        throw new Error('No OAuth URL returned from server');
      }

      return { url: authUrl };

    } catch (error: any) {
      console.error('GitHub OAuth URL error:', error);

      // Re-throw with more context
      if (error.response) {
        throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        throw new Error('Network error - could not reach authentication server');
      } else {
        throw error;
      }
    }
  }

  // OAuth callback endpoints with better error handling
  async processGoogleCallback(code: string, state?: string): Promise<AuthResponsed> {
    try {
      const response: AxiosResponse<AuthResponsed> = await this.api.post(
        '/api/auth/google/callback',
        { code, state },
        { withCredentials: true }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Google authentication failed');
      }

      return response.data;

    } catch (error: any) {
      console.error('Google callback error:', error);

      if (error.response) {
        throw new Error(error.response.data?.message || 'Google authentication failed');
      } else if (error.request) {
        throw new Error('Network error during Google authentication');
      } else {
        throw error;
      }
    }
  }

  async processGitHubCallback(code: string, state?: string): Promise<AuthResponsed> {
    try {
      const response: AxiosResponse<AuthResponsed> = await this.api.post(
        '/api/auth/github/callback',
        { code, state },
        { withCredentials: true }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'GitHub authentication failed');
      }

      return response.data;

    } catch (error: any) {
      console.error('GitHub callback error:', error);

      if (error.response) {
        throw new Error(error.response.data?.message || 'GitHub authentication failed');
      } else if (error.request) {
        throw new Error('Network error during GitHub authentication');
      } else {
        throw error;
      }
    }
  }

  // Generic method for OAuth callbacks
  async processOAuthCallback(
    provider: 'google' | 'github',
    code: string,
    state?: string
  ): Promise<AuthResponsed> {
    try {
      const response: AxiosResponse<AuthResponsed> = await this.api.post(
        `/api/auth/${provider}/callback`,
        { code, state },
        { withCredentials: true }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || `${provider} authentication failed`);
      }

      return response.data;

    } catch (error: any) {
      console.error(`${provider} callback error:`, error);

      if (error.response) {
        throw new Error(error.response.data?.message || `${provider} authentication failed`);
      } else if (error.request) {
        throw new Error(`Network error during ${provider} authentication`);
      } else {
        throw error;
      }
    }
  }
  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.get('/api/auth/me');
    return response.data;
  }

  async verifyEmail(otp: string): Promise<ApiResponse<User>> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.post('/api/auth/verify-email', { otp });
    return response.data;
  }

  async resendVerificationEmail(): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post('/api/auth/resend-verification');
    return response.data;
  }

  async forgotPassword(email: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post('/api/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, password: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post('/api/auth/reset-password', { token, password });
    return response.data;
  }

  async resendOTP(email: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post('/api/auth/resend-otp', { email });
    return response.data;
  }

  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post('/api/auth/change-password', data);
    return response.data;
  }

  async loginWithOTP(email: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post('/api/auth/login-otp', { email });
    return response.data;
  }

  async verifyOTP(email: string, otp: string): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/api/auth/verify-otp', { email, otp });
    return response.data;
  }

  // Hackathon endpoints
  async getHackathons(params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    status?: string;
    location?: string;
    isFeatured?: boolean;
    registrationFees?: string;
    organizer?: string;
  }): Promise<PaginatedResponse<Hackathon>> {
    // Remove empty or undefined params (type-safe)
    const filteredParams = Object.fromEntries(
      Object.entries(params || {}).filter(([_, v]) => v !== '' && v !== undefined)
    ) as typeof params;
    const response: AxiosResponse<PaginatedResponse<Hackathon>> = await this.api.get('/api/hackathons', { params: filteredParams });
    return response.data;
  }

  async getHackathon(id: string): Promise<ApiResponse<Hackathon>> {
    const response: AxiosResponse<ApiResponse<Hackathon>> = await this.api.get(`/api/hackathons/${id}`);
    return response.data;
  }

  async checkHackathonUsername(username: string): Promise<ApiResponse<{ available: boolean; message: string }>> {
    const response: AxiosResponse<ApiResponse<{ available: boolean; message: string }>> = await this.api.get(`/api/hackathons/check-username/${username}`);
    return response.data;
  }


  async uploadHackathonImage(file: File): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/api/hackathons/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async createHackathon(hackathonData: Partial<Hackathon>): Promise<ApiResponse<Hackathon>> {
    const response: AxiosResponse<ApiResponse<Hackathon>> = await this.api.post('/api/hackathons', hackathonData);
    return response.data;
  }

  async updateHackathon(id: string, hackathonData: Partial<Hackathon>): Promise<ApiResponse<Hackathon>> {
    const response: AxiosResponse<ApiResponse<Hackathon>> = await this.api.put(`/api/hackathons/${id}`, hackathonData);
    return response.data;
  }

  async deleteHackathon(id: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.delete(`/api/hackathons/${id}`);
    return response.data;
  }

  async registerForHackathon(id: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post(`/api/hackathons/${id}/register`);
    return response.data;
  }

  async unregisterFromHackathon(id: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.delete(`/api/hackathons/${id}/register`);
    return response.data;
  }
  // Add to your apiService class
  async submitTeamPresentation(hackathonId: string, teamId: string, submissionData: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/api/hackathons/${hackathonId}/teams/${teamId}/submit-presentation`,
      submissionData
    );
    return response.data;
  }
  // Optional: Get team details by ID (if needed for direct team access)
  async getHackathonTeam(hackathonId: string, teamId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/hackathons/${hackathonId}/teams/${teamId}`);
    return response.data;
  }
  async leaveHackathonTeam(hackathonId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${hackathonId}/teams/leave`);
    return response.data;
  }
  // Optional: Update team name (if you want to allow editing)
  async updateHackathonTeam(hackathonId: string, teamId: string, updates: { name?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(`/api/hackathons/${hackathonId}/teams/${teamId}`, updates);
    return response.data;
  }
  async createHackathonTeam(hackathonId: string, name: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${hackathonId}/teams`, { name });
    return response.data;
  }

  async joinHackathonTeam(hackathonId: string, code: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${hackathonId}/teams/join`, { code });
    return response.data;
  }

  async getMyHackathonTeam(hackathonId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/hackathons/${hackathonId}/teams/me`);
    return response.data;
  }

  async removeHackathonTeamMember(hackathonId: string, teamId: string, memberId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${hackathonId}/teams/${teamId}/remove`, { memberId });
    return response.data;
  }

  async deleteHackathonTeam(hackathonId: string, teamId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${hackathonId}/teams/${teamId}/delete`);
    return response.data;
  }

  async submitHackathonTeam(hackathonId: string, teamId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${hackathonId}/teams/${teamId}/submit`);
    return response.data;
  }

  // async getFeaturedHackathons(limit: number = 8): Promise<ApiResponse<Hackathon[]>> {
  //   const response: AxiosResponse<ApiResponse<Hackathon[]>> = await this.api.get('/api/hackathons/?featured=true', { 
  //     params: { limit } 
  //   });
  //   return response.data;
  // }
  async getFeaturedHackathons(limit: number = 8): Promise<ApiResponse<Hackathon[]>> {
    // Fetch more hackathons than needed to account for filtering
    const response: AxiosResponse<ApiResponse<Hackathon[]>> = await this.api.get('/api/hackathons/?featured=true', {
      params: { limit: limit * 2 } // Fetch double to ensure we have enough after filtering
    });

    const now = new Date();

    // Check if data exists before filtering
    if (!response.data.data) {
      return response.data;
    }

    // Filter hackathons with valid registration deadlines
    const filteredHackathons = response.data.data.filter(hackathon => {
      // Check if registration deadline exists and hasn't passed
      if (hackathon.dates?.registrationDeadline) {
        const deadline = new Date(hackathon.dates.registrationDeadline);
        return deadline >= now;
      }

      // If no registration deadline, check if start date is in the future
      if (hackathon.dates?.startDate) {
        const startDate = new Date(hackathon.dates.startDate);
        return startDate >= now;
      }

      // If neither deadline nor start date, include it
      return true;
    }).slice(0, limit); // Limit to requested number

    return {
      ...response.data,
      data: filteredHackathons
    };
  }

  // Get hackathon statistics for live counters
  async getHackathonStatistics(hackathonId: string): Promise<ApiResponse<{
    totalRegistrations: number;
    teamsCreated: number;
    submissionsReceived: number;
    approvedRound1: number;
    approvedRound2: number;
  }>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/hackathons/${hackathonId}/statistics`);
    return response.data;
  }

  // Get all hackathon submissions (for organizers/admins)
  async getHackathonSubmissions(hackathonId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/hackathons/${hackathonId}/submissions`);
    return response.data;
  }

  // Approve team for Round 1
  async approveSubmissionRound1(hackathonId: string, teamId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/api/hackathons/${hackathonId}/teams/${teamId}/approve-round1`
    );
    return response.data;
  }

  // Approve team for Round 2
  async approveSubmissionRound2(hackathonId: string, teamId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/api/hackathons/${hackathonId}/teams/${teamId}/approve-round2`
    );
    return response.data;
  }

  // Reject team submission
  async rejectSubmission(hackathonId: string, teamId: string, reason?: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/api/hackathons/${hackathonId}/teams/${teamId}/reject`,
      { reason }
    );
    return response.data;
  }

  // Get approved teams by round
  async getApprovedTeams(hackathonId: string, round: number): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/api/hackathons/${hackathonId}/approved-teams?round=${round}`
    );
    return response.data;
  }

  // Event endpoints
  async getEvents(params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    eventType?: string;
    status?: string;
    location?: string;
    isFeatured?: boolean;
    organizer?: string;
  }): Promise<PaginatedResponse<Event>> {
    // Remove empty or undefined params (type-safe)
    const filteredParams = Object.fromEntries(
      Object.entries(params || {}).filter(([_, v]) => v !== '' && v !== undefined)
    ) as typeof params;
    const response: AxiosResponse<PaginatedResponse<Event>> = await this.api.get('/api/events', { params: filteredParams });
    return response.data;
  }

  async getEvent(id: string): Promise<ApiResponse<Event>> {
    const response: AxiosResponse<ApiResponse<Event>> = await this.api.get(`/api/events/${id}`);
    return response.data;
  }



  async checkEventUsername(username: string): Promise<ApiResponse<{ available: boolean; message: string }>> {
    const response: AxiosResponse<ApiResponse<{ available: boolean; message: string }>> = await this.api.get(`/api/events/check-username/${username}`);
    return response.data;
  }

  async createEvent(eventData: Partial<Event>): Promise<ApiResponse<Event>> {
    const response: AxiosResponse<ApiResponse<Event>> = await this.api.post('/api/events', eventData);
    return response.data;
  }

  async updateEvent(id: string, eventData: Partial<Event>): Promise<ApiResponse<Event>> {
    const response: AxiosResponse<ApiResponse<Event>> = await this.api.put(`/api/events/${id}`, eventData);
    return response.data;
  }

  async deleteEvent(id: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.delete(`/api/events/${id}`);
    return response.data;
  }

  async registerForEvent(id: string, profileData?: {
    firstName?: string;
    lastName?: string;
    college?: string;
    course?: string;
    branch?: string;
    collegeYear?: string;
    country?: string;
    state?: string;
    agreeToTerms?: boolean;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/events/${id}/register`, profileData || {});
    return response.data;
  }

  async unregisterFromEvent(id: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.delete(`/api/events/${id}/register`);
    return response.data;
  }

  async getFeaturedEvents(limit: number = 8): Promise<ApiResponse<Event[]>> {
    const response: AxiosResponse<ApiResponse<Event[]>> = await this.api.get('/api/events/featured', {
      params: { limit }
    });
    return response.data;
  }

  // Saved Hackathons/Events endpoints
  async saveHackathon(username: string): Promise<ApiResponse<string[]>> {
    const response: AxiosResponse<ApiResponse<string[]>> = await this.api.post(`/api/users/save-hackathon/${username}`);
    return response.data;
  }
  async unsaveHackathon(username: string): Promise<ApiResponse<string[]>> {
    const response: AxiosResponse<ApiResponse<string[]>> = await this.api.delete(`/api/users/save-hackathon/${username}`);
    return response.data;
  }
  async getSavedHackathons(): Promise<ApiResponse<Hackathon[]>> {
    const response: AxiosResponse<ApiResponse<Hackathon[]>> = await this.api.get('/api/users/saved-hackathons');
    return response.data;
  }
  async saveEvent(username: string): Promise<ApiResponse<string[]>> {
    const response: AxiosResponse<ApiResponse<string[]>> = await this.api.post(`/api/users/save-event/${username}`);
    return response.data;
  }
  async unsaveEvent(username: string): Promise<ApiResponse<string[]>> {
    const response: AxiosResponse<ApiResponse<string[]>> = await this.api.delete(`/api/users/save-event/${username}`);
    return response.data;
  }
  async getSavedEvents(): Promise<ApiResponse<Event[]>> {
    const response: AxiosResponse<ApiResponse<Event[]>> = await this.api.get('/api/users/saved-events');
    return response.data;
  }

  // Quiz endpoints
  async createQuiz(quizData: Partial<Quiz>): Promise<ApiResponse<Quiz>> {
    const response: AxiosResponse<ApiResponse<Quiz>> = await this.api.post('/api/quizzes', quizData);
    return response.data;
  }

  async getQuizzes(params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    organizer?: string;
  }): Promise<PaginatedResponse<Quiz>> {
    const filteredParams = Object.fromEntries(
      Object.entries(params || {}).filter(([_, v]) => v !== '' && v !== undefined)
    ) as typeof params;
    const response: AxiosResponse<PaginatedResponse<Quiz>> = await this.api.get('/api/quizzes', { params: filteredParams });
    return response.data;
  }

  async getMyQuizzes(): Promise<ApiResponse<Quiz[]>> {
    const response: AxiosResponse<ApiResponse<Quiz[]>> = await this.api.get('/api/quizzes/my-quizzes');
    return response.data;
  }

  // New: Get all hackathons created by the current user (no status filter)
  async getMyHackathons(): Promise<PaginatedResponse<Hackathon>> {
    const response: AxiosResponse<PaginatedResponse<Hackathon>> = await this.api.get('/api/hackathons/my-hackathons');
    return response.data;
  }

  // New: Get all events created by the current user (no status filter)
  async getMyEvents(): Promise<ApiResponse<Event[]>> {
    const response: AxiosResponse<ApiResponse<Event[]>> = await this.api.get('/api/events/my-events');
    return response.data;
  }

  async getFeaturedQuizzes(limit: number = 8): Promise<ApiResponse<Quiz[]>> {
    const response: AxiosResponse<ApiResponse<Quiz[]>> = await this.api.get('/api/quizzes/featured', {
      params: { limit }
    });
    return response.data;
  }

  async getQuiz(id: string): Promise<ApiResponse<Quiz>> {
    const response: AxiosResponse<ApiResponse<Quiz>> = await this.api.get(`/api/quizzes/${id}`);
    return response.data;
  }

  async getQuizQuestions(id: string): Promise<ApiResponse<{
    title: string;
    timeLimit: number;
    questions: Array<{
      question: string;
      options: string[];
      points: number;
    }>;
  }>> {
    const response: AxiosResponse<ApiResponse<{
      title: string;
      timeLimit: number;
      questions: Array<{
        question: string;
        options: string[];
        points: number;
      }>;
    }>> = await this.api.get(`/api/quizzes/${id}/questions`);
    return response.data;
  }

  async submitQuiz(id: string, data: {
    answers: Array<{
      questionIndex: number;
      selectedAnswer: number;
    }>;
    timeTaken: number;
  }): Promise<ApiResponse<QuizResult>> {
    const response: AxiosResponse<ApiResponse<QuizResult>> = await this.api.post(`/api/quizzes/${id}/submit`, data);
    return response.data;
  }

  async getQuizResults(id: string): Promise<ApiResponse<Quiz>> {
    const response: AxiosResponse<ApiResponse<Quiz>> = await this.api.get(`/api/quizzes/${id}/results`);
    return response.data;
  }

  async updateQuiz(id: string, quizData: Partial<Quiz>): Promise<ApiResponse<Quiz>> {
    const response: AxiosResponse<ApiResponse<Quiz>> = await this.api.put(`/api/quizzes/${id}`, quizData);
    return response.data;
  }

  async deleteQuiz(id: string): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.delete(`/api/quizzes/${id}`);
    return response.data;
  }

  // Upload image directly (for quiz creation)
  async uploadImageDirectly(file: File): Promise<ApiResponse<{ image: string }>> {
    const formData = new FormData();
    formData.append('image', file);

    const response: AxiosResponse<ApiResponse<{ image: string }>> = await this.api.post('/api/quizzes/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async uploadQuizImage(id: string, file: File): Promise<ApiResponse<{ image: string }>> {
    const formData = new FormData();
    formData.append('image', file);

    const response: AxiosResponse<ApiResponse<{ image: string }>> = await this.api.post(`/api/quizzes/${id}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async registerForQuiz(id: string, agreeToTerms: boolean, profileData?: {
    firstName?: string;
    lastName?: string;
    college?: string;
    course?: string;
    branch?: string;
    collegeYear?: string;
    country?: string;
    state?: string;
    referralCode?: string;
  }): Promise<ApiResponse<{
    requiresPayment: boolean;
    amount: number;
    currency: string;
  }>> {
    const requestData = { agreeToTerms, ...profileData };
    const response: AxiosResponse<ApiResponse<{
      requiresPayment: boolean;
      amount: number;
      currency: string;
    }>> = await this.api.post(`/api/quizzes/${id}/register`, requestData);
    return response.data;
  }

  async getQuizRegistrationStatus(id: string): Promise<ApiResponse<{
    isRegistered: boolean;
    registrationOpen: boolean;
    quizRunning: boolean;
    participant: {
      paymentStatus: string;
      registrationDate: string;
    } | null;
    pricing: {
      type: string;
      amount: number;
      currency: string;
    };
  }>> {
    const response: AxiosResponse<ApiResponse<{
      isRegistered: boolean;
      registrationOpen: boolean;
      quizRunning: boolean;
      participant: {
        paymentStatus: string;
        registrationDate: string;
      } | null;
      pricing: {
        type: string;
        amount: number;
        currency: string;
      };
    }>> = await this.api.get(`/api/quizzes/${id}/registration-status`);
    return response.data;
  }

  async createPaymentOrder(id: string): Promise<ApiResponse<{
    orderId: string;
    amount: number;
    currency: string;
    key: string;
  }>> {
    const response: AxiosResponse<ApiResponse<{
      orderId: string;
      amount: number;
      currency: string;
      key: string;
    }>> = await this.api.post(`/api/quizzes/${id}/create-payment-order`);
    return response.data;
  }

  async verifyPayment(id: string, data: {
    orderId: string;
    paymentId: string;
    signature: string;
  }): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post(`/api/quizzes/${id}/verify-payment`, data);
    return response.data;
  }

  async getRegisteredQuizzes(): Promise<ApiResponse<Quiz[]>> {
    const response: AxiosResponse<ApiResponse<Quiz[]>> = await this.api.get('/api/quizzes/user/registered');
    return response.data;
  }

  async getRegisteredHackathons(): Promise<ApiResponse<Hackathon[]>> {
    const response: AxiosResponse<ApiResponse<Hackathon[]>> = await this.api.get('/api/hackathons/user/registered');
    return response.data;
  }

  async getRegisteredEvents(): Promise<ApiResponse<Event[]>> {
    const response: AxiosResponse<ApiResponse<Event[]>> = await this.api.get('/api/events/user/registered');
    return response.data;
  }

  async getMyCertificates(): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/api/certificates/my-certificates');
    return response.data;
  }

  // User endpoints
  async updateProfile(userData: Partial<User>): Promise<ApiResponse<User>> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.put('/api/users/profile', userData);
    return response.data;
  }

  async requestAccountDeletion(data: { email: string; reason?: string }): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post('/api/users/deletion-request', data);
    return response.data;
  }

  async uploadProfilePicture(file: File): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('profilePicture', file);

    const response: AxiosResponse<ApiResponse<{ url: string }>> = await this.api.post('/api/users/profile-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getUserProfile(username: string): Promise<ApiResponse<User>> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.get(`/api/users/${username}`);
    return response.data;
  }


  // User profile and search methods
  async getUserProfiles(): Promise<ApiResponse<User[]>> {
    const response: AxiosResponse<ApiResponse<User[]>> =
      await this.api.get('/api/users/profile');
    return response.data;
  }

  // Fix the API service methods for user profile and search



  // 2. Update the searchUser method to match backend endpoint and accept filters
  async searchUser(
    query: string = '',
    page: number = 1,
    filters?: UserFilters
  ): Promise<ApiResponse<{ users: User[], total: number, page: number, totalPages: number }>> {
    const params = new URLSearchParams({
      q: query,
      page: page.toString(),
      limit: '20'
    });

    // Add filters if provided
    if (filters) {
      if (filters.college && filters.college.trim()) {
        params.append('college', filters.college.trim());
      }
      if (filters.collegeYear && filters.collegeYear !== 'all') {
        params.append('collegeYear', filters.collegeYear);
      }
      if (filters.branch && filters.branch.trim()) {
        params.append('branch', filters.branch.trim());
      }
      if (filters.gender && filters.gender !== 'all') {
        params.append('gender', filters.gender);
      }
      if (filters.hackathonName && filters.hackathonName.trim()) {
        params.append('hackathonName', filters.hackathonName.trim());
      }
      if (filters.states && filters.states.length > 0) {
        params.append('states', filters.states.join(','));
      }
      if (filters.skills && filters.skills.length > 0) {
        // Backend expects comma separated string for skills too?
        // Backend Line 169: `skills.split(',')`.
        // Client Line 859: `filters.skills.forEach(skill => params.append('skills', skill));`
        // `URLSearchParams` with multiple values for same key?
        // Express `req.query` with `skills` appearing multiple times results in an array.
        // Backend Line 169 calls `.split(',')` on `skills`.
        // If `skills` is ['React', 'Node'], `.split` fails.
        // So Backend expects a STRING.
        // Client SHOULD join skills.
        params.append('skills', filters.skills.join(','));
      }
      // Add intent for discover endpoint
      if (filters.intent && filters.intent.trim()) {
        params.append('intent', filters.intent.trim());
      }
    }

    // Use /api/users/discover endpoint which supports intent-based browsing
    const response: AxiosResponse<any> =
      await this.api.get(`/api/users/discover?${params.toString()}`);

    // Map backend response format to consistent frontend structure
    return {
      success: response.data.success,
      message: response.data.message,
      data: {
        users: response.data.users || [],
        total: response.data.count || 0,
        page: response.data.pagination?.current || 1,
        totalPages: response.data.pagination?.total || 1
      }
    };
  }

  // 3. Keep the updateUserProfile method as is
  async updateUserProfile(profileData: UserProfile): Promise<ApiResponse<User>> {
    const response: AxiosResponse<ApiResponse<User>> =
      await this.api.put('/api/users/profile', profileData);
    return response.data;
  }

  // User endpoints for saved quizzes
  async saveQuiz(quizId: string): Promise<ApiResponse<string[]>> {
    const response: AxiosResponse<ApiResponse<string[]>> = await this.api.post(`/api/users/save-quiz/${quizId}`);
    return response.data;
  }
  async unsaveQuiz(quizId: string): Promise<ApiResponse<string[]>> {
    const response: AxiosResponse<ApiResponse<string[]>> = await this.api.delete(`/api/users/save-quiz/${quizId}`);
    return response.data;
  }
  async getSavedQuizzes(): Promise<ApiResponse<Quiz[]>> {
    const response: AxiosResponse<ApiResponse<Quiz[]>> = await this.api.get('/api/users/saved-quizzes');
    return response.data;
  }

  async getQuizLeaderboard(id: string): Promise<ApiResponse<{
    leaderboard: any[];
    userResult: any;
  }>> {
    const response: AxiosResponse<ApiResponse<{
      leaderboard: any[];
      userResult: any;
    }>> = await this.api.get(`/api/quizzes/${id}/leaderboard`);
    return response.data;
  }

  async getEventRegistrationStatus(id: string): Promise<ApiResponse<{
    registered: boolean;
    paymentStatus: string | null;
  }>> {
    const response: AxiosResponse<ApiResponse<{ registered: boolean; paymentStatus: string | null }>> = await this.api.get(`/api/events/${id}/registration-status`);
    return response.data;
  }

  async createEventPaymentOrder(id: string): Promise<ApiResponse<{ orderId: string; amount: number; currency: string; key: string }>> {
    const response: AxiosResponse<ApiResponse<{ orderId: string; amount: number; currency: string; key: string }>> = await this.api.post(`/api/events/${id}/create-payment-order`);
    return response.data;
  }

  async verifyEventPayment(id: string, data: { orderId: string; paymentId: string; signature: string; }): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post(`/api/events/${id}/verify-payment`, data);
    return response.data;
  }

  async createHackathonPaymentOrder(id: string): Promise<ApiResponse<{ orderId: string; amount: number; currency: string; key: string }>> {
    const response: AxiosResponse<ApiResponse<{ orderId: string; amount: number; currency: string; key: string }>> = await this.api.post(`/api/hackathons/${id}/create-payment-order`);
    return response.data;
  }

  async verifyHackathonPayment(id: string, data: { orderId: string; paymentId: string; signature: string; }): Promise<ApiResponse<null>> {
    const response: AxiosResponse<ApiResponse<null>> = await this.api.post(`/api/hackathons/${id}/verify-payment`, data);
    return response.data;
  }


  async getHackathonRegistrationStatus(id: string): Promise<ApiResponse<{
    registered: boolean;
    paymentStatus: string | null;
    registrationComplete?: boolean;
    hasCustomQuestions?: boolean;
    basicInfoSubmitted?: boolean;
    additionalAnswersSubmitted?: boolean;
  }>> {
    const response: AxiosResponse<ApiResponse<{
      registered: boolean;
      paymentStatus: string | null;
      registrationComplete?: boolean;
      hasCustomQuestions?: boolean;
      basicInfoSubmitted?: boolean;
      additionalAnswersSubmitted?: boolean;
    }>> = await this.api.get(`/api/hackathons/${id}/registration-status`);
    return response.data;
  }


  // Add these to your apiService
  async getHackathonRegistrationDetails(id: string): Promise<ApiResponse<RegistrationDetails>> {
    const response = await this.api.get(`/api/hackathons/${id}/registration-details`);
    return response.data;
  }

  async getEventRegistrationDetails(id: string): Promise<ApiResponse<RegistrationDetails>> {
    const response = await this.api.get(`/api/events/${id}/registration-details`);
    return response.data;
  }

  async getQuizRegistrationDetails(id: string): Promise<ApiResponse<RegistrationDetails>> {
    const response = await this.api.get(`/api/quizzes/${id}/registration-details`);
    return response.data;
  }

  async registerForHackathonWithProfile(id: string, profileData?: {
    firstName?: string;
    lastName?: string;
    college?: string;
    course?: string;
    branch?: string;
    collegeYear?: string;
    country?: string;
    state?: string;
    agreeToTerms?: boolean;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${id}/register`, profileData || {});
    return response.data;
  }

  // Review and Rating endpoints
  async addHackathonReview(id: string, data: { rating: number; comment?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${id}/reviews`, data);
    return response.data;
  }

  async updateHackathonReview(id: string, data: { rating: number; comment?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/hackathons/${id}/reviews`, data);
    return response.data;
  }

  async deleteHackathonReview(id: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/hackathons/${id}/reviews`);
    return response.data;
  }

  async getHackathonReviews(id: string, page?: number, limit?: number): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());

    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/hackathons/${id}/reviews?${params.toString()}`);
    return response.data;
  }
  // Add these methods to your apiService class

  // Announcement methods
  async createAnnouncement(eventId: string, data: { title: string; message: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/events/${eventId}/announcements`, data);
    return response.data;
  }

  async getAnnouncements(eventId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/events/${eventId}/announcements`);
    return response.data;
  }

  async updateAnnouncement(eventId: string, announcementId: string, data: { title?: string; message?: string }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/events/${eventId}/announcements/${announcementId}`, data);
    return response.data;
  }

  async deleteAnnouncement(eventId: string, announcementId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/events/${eventId}/announcements/${announcementId}`);
    return response.data;
  }
  // Project Management
  async addProject(data: {
    title: string;
    description: string;
    technologies?: string[];
    githubUrl?: string;
    liveUrl?: string;
    image?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post('/api/users/projects', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add project'
      };
    }
  }

  async getUserProjects(username: string): Promise<{ success: boolean; projects: Project[] }> {
    try {
      const response = await this.api.get(`/api/users/projects?username=${username}`);
      return response.data;
    } catch (error: any) {
      // Optionally log error.message or show a toast here
      return {
        success: false,
        projects: []
      };
    }
  }

  // Fixed API service method
  async getProjects(projectId: string): Promise<{ success: boolean; data: Project | null; message?: string }> {
    try {
      const response = await this.api.get(`/api/users/projects/${projectId}`);

      // Handle the response structure properly
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data, // Now matches backend response
          message: response.data.message
        };
      } else {
        return {
          success: false,
          data: null,
          message: response.data.message || 'Failed to fetch project'
        };
      }
    } catch (error: any) {
      console.error('API Error:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || 'Network error'
      };
    }
  }

  // Add these methods to your API service class

  async getSavedProjects(): Promise<ApiResponse<Project[]>> {
    try {
      console.log('Making API call to /api/users/saved-projects');
      const response = await this.api.get('/api/users/saved-projects');
      console.log('Raw API response:', response);
      console.log('Response data:', response.data);

      // Ensure we're returning the correct data structure
      if (response.data && response.data.success) {
        return response.data;
      } else {
        return {
          success: false,
          data: [],
          message: 'Invalid response format'
        };
      }
    } catch (error: any) {
      console.error('Error fetching saved projects:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Failed to fetch saved projects'
      };
    }
  }


  async getSavedProjectsCount(): Promise<ApiResponse<{ count: number }>> {
    try {
      const response = await this.api.get('/api/users/saved-projects/count');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching saved projects count:', error);
      return {
        success: false,
        data: { count: 0 },
        message: error.response?.data?.message || 'Failed to fetch saved projects count'
      };
    }
  }


  async updateProject(projectId: string, data: {
    title: string;
    description: string;
    technologies?: string[];
    githubUrl?: string;
    liveUrl?: string;
    image?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.put(`/api/users/projects/${projectId}`, data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update project'
      };
    }
  }

  async deleteProject(projectId: string): Promise<ApiResponse<any>> {
    try {
      // eslint-disable-next-line no-restricted-globals
      if (!confirm('Are you sure you want to delete this project?')) {
        return {
          success: false,
          message: 'User cancelled project deletion'
        };
      }
      const response = await this.api.delete(`/api/users/projects/${projectId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete project'
      };
    }
  }

  // Project Social Actions
  // Project Social Actions
  async likeProject(projectId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post(`/api/users/projects/${projectId}/like`);
      return response.data;
    } catch (error: any) {
      console.error('Error liking project:', error);
      throw error;
    }
  }

  async unlikeProject(projectId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post(`/api/users/projects/${projectId}/unlike`);
      return response.data;
    } catch (error: any) {
      console.error('Error unliking project:', error);
      throw error;
    }
  }

  async commentOnProject(projectId: string, text: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post(`/api/users/projects/${projectId}/comments`, { text });
      return response.data;
    } catch (error: any) {
      console.error('Error commenting on project:', error);
      throw error;
    }
  }

  async replyToProjectComment(projectId: string, commentId: string, text: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post(`/api/users/projects/${projectId}/comments/${commentId}/replies`, { text });
      return response.data;
    } catch (error: any) {
      console.error('Error replying to comment:', error);
      throw error;
    }
  }

  async saveProject(projectId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post(`/api/users/projects/${projectId}/save`);
      return response.data;
    } catch (error: any) {
      console.error('Error saving project:', error);
      throw error;
    }
  }

  async unsaveProject(projectId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post(`/api/users/projects/${projectId}/unsave`);
      return response.data;
    } catch (error: any) {
      console.error('Error unsaving project:', error);
      throw error;
    }
  }
  // Hackathon Announcements
  async addHackathonAnnouncement(hackathonId: string, announcementData: any): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/hackathons/${hackathonId}/announcements`, announcementData);
    return response.data;
  }

  async getHackathonAnnouncements(hackathonId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/hackathons/${hackathonId}/announcements`);
    return response.data;
  }

  async updateHackathonAnnouncement(hackathonId: string, announcementId: string, announcementData: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/hackathons/${hackathonId}/announcements/${announcementId}`, announcementData);
    return response.data;
  }

  async deleteHackathonAnnouncement(hackathonId: string, announcementId: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/hackathons/${hackathonId}/announcements/${announcementId}`);
    return response.data;
  }

  async updateTeamStatus(hackathonId: string, teamId: string, action: 'approve' | 'reject' | 'disqualify' | 'reset', roundIndex?: number, note?: string): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/hackathons/${hackathonId}/teams/${teamId}/status`, { action, roundIndex, note });
    return response.data;
  }

  async getHackathonParticipants(hackathonId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/community/${hackathonId}/participants`);
    return response.data;
  }

  // ✅ Correct + consistent API methods
  // (getHackathonSubmissions already defined earlier in the file at line 442)

  // Get hackathon participants
  async getHackathonParticipant(hackathonId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/hackathons/${hackathonId}/participants`);
    return response.data;
  }

  // Get hackathon registration details for a user
  async getHackathonRegistrationDetail(hackathonId: string, userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/hackathons/${hackathonId}/registration-details/${userId}`);
    return response.data;
  }

  // Submit payment withdrawal request
  async submitHackathonPaymentWithdrawRequest(hackathonId: string, requestData: any): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/hackathons/${hackathonId}/payment-withdrawal-request`, requestData);
    return response.data;
  }

  // Get payment withdrawal status (by hackathon username)
  async getHackathonPaymentWithdrawalStatus(hackathonUsername: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/hackathons/${hackathonUsername}/payment-withdrawal-status`);
    return response.data;
  }



  // Sponsor Management
  async addHackathonSponsor(hackathonId: string, sponsorData: any): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/hackathons/${hackathonId}/sponsors`, sponsorData);
    return response.data;
  }

  async getHackathonSponsors(hackathonId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/hackathons/${hackathonId}/sponsors`);
    return response.data;
  }

  async updateHackathonSponsor(hackathonId: string, sponsorId: string, sponsorData: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/hackathons/${hackathonId}/sponsors/${sponsorId}`, sponsorData);
    return response.data;
  }

  async deleteHackathonSponsor(hackathonId: string, sponsorId: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/hackathons/${hackathonId}/sponsors/${sponsorId}`);
    return response.data;
  }

  // Event Participants
  async getEventParticipants(eventId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/events/${eventId}/participants`);
    return response.data;
  }



  // Get registration details for a specific user (for organizers only)
  async getEventRegistrationDetail(eventId: string, userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/events/${eventId}/registration-details/${userId}`);
    return response.data;
  }

  // Submit payment withdrawal request
  async submitPaymentWithdrawRequest(eventId: string, withdrawalData: {
    upiId: string;
    phoneNumber: string;
    confirmUpiId: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/events/${eventId}/payment-withdrawal-request`, {
      upiId: withdrawalData.upiId,
      phoneNumber: withdrawalData.phoneNumber
    });
    return response.data;
  }

  // Get payment withdrawal status
  async getPaymentWithdrawalStatus(eventId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/events/${eventId}/payment-withdrawal-status`);
    return response.data;
  }

  // Get event registration details for current user
  async getMyEventRegistrationDetails(eventId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/events/${eventId}/registration-details`);
    return response.data;
  }


  // Get Event by Username
  // async getEventByUsername(username: string): Promise<ApiResponse<any>> {
  //   const response = await this.api.get(`/api/events/username/${username}`);
  //   return response.data;
  // }
  async getEventByUsername(username: string): Promise<ApiResponse<Event>> {
    const response: AxiosResponse<ApiResponse<Event>> = await this.api.get(`/api/events/${username}`);
    return response.data;
  }
  // Get Hackathon by Username
  async getHackathonByUsername(username: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/hackathons/username/${username}`);
    return response.data;
  }

  // Quiz Participants
  async getQuizParticipants(quizId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/quizzes/${quizId}/participants`);
    return response.data;
  }

  // Add these methods to your apiService.js file


  // Get registration details for a specific user (for quiz creators only)
  async getQuizRegistrationDetail(quizId: string, userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/quizzes/${quizId}/registration-details/${userId}`);
    return response.data;
  }

  // Submit payment withdrawal request for quiz
  async submitQuizPaymentWithdrawRequest(quizId: string, withdrawalData: {
    upiId: string;
    phoneNumber: string;
    confirmUpiId: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/quizzes/${quizId}/payment-withdrawal-request`, {
      upiId: withdrawalData.upiId,
      phoneNumber: withdrawalData.phoneNumber
    });
    return response.data;
  }

  // Get payment withdrawal status for quiz (using quiz ID instead of username)
  async getQuizPaymentWithdrawalStatus(quizId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/quizzes/${quizId}/payment-withdrawal-status`);
    return response.data;
  }

  // Get quiz registration details for current user
  async getMyQuizRegistrationDetails(quizId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/quizzes/${quizId}/registration-details`);
    return response.data;
  }

  // Social networking endpoints
  async followUser(userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/users/${userId}/follow`);
    return response.data;
  }
  async unfollowUser(userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/users/${userId}/unfollow`);
    return response.data;
  }
  async sendConnectionRequest(userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/users/${userId}/connect`);
    return response.data;
  }
  async acceptConnectionRequest(userId: string): Promise<ApiResponse<any>> {
    console.log('API: Accepting connection request for userId:', userId);
    const response = await this.api.post(`/api/users/${userId}/accept-connection`);
    return response.data;
  }
  async rejectConnectionRequest(userId: string): Promise<ApiResponse<any>> {
    console.log('API: Rejecting connection request for userId:', userId);
    const response = await this.api.post(`/api/users/${userId}/reject-connection`);
    return response.data;
  }
  async getRelationshipStatus(userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/users/${userId}/relationship`);
    return response.data;
  }

  async getChatHistory(userId: string, options?: { limit?: number; before?: string }): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.before) params.append('before', options.before);
    const response = await this.api.get(`/api/users/${userId}/chat?${params.toString()}`);
    return response.data;
  }

  async getMessageThreads(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/users/messages/threads');
    return response.data;
  }
  async getUserNotifications(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/users/notifications');
    return response.data;
  }

  async getUnreadMessageCount(): Promise<ApiResponse<{ count: number }>> {
    const response = await this.api.get('/api/users/messages/unread-count');
    return response.data;
  }

  async markMessagesAsRead(userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/users/messages/mark-read/${userId}`);
    return response.data;
  }

  async markAllNotificationsAsRead(): Promise<ApiResponse<any>> {
    const response = await this.api.patch('/api/notifications/mark-all-read');
    return response.data;
  }

  async markNotificationAsRead(notificationId: string): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/api/users/notifications/${notificationId}/read`);
    return response.data;
  }

  async deleteNotification(notificationId: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/users/notifications/${notificationId}`);
    return response.data;
  }

  async getConnectedUsers(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/users/connections');
    return response.data;
  }

  // Community chat methods
  async getCommunityMessages(hackathonId: string, page?: number, limit?: number): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    const response = await this.api.get(`/api/community/${hackathonId}/messages?${params.toString()}`);
    return response.data;
  }

  async sendCommunityMessage(hackathonId: string, message: string, replyTo?: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/community/${hackathonId}/messages`, { message, replyTo });
    return response.data;
  }

  async editCommunityMessage(hackathonId: string, messageId: string, message: string): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/community/${hackathonId}/messages/${messageId}`, { message });
    return response.data;
  }

  async deleteCommunityMessage(hackathonId: string, messageId: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/community/${hackathonId}/messages/${messageId}`);
    return response.data;
  }

  async getCommunityAdmins(hackathonId: string): Promise<ApiResponse<any[]>> {
    const response = await this.api.get(`/api/community/${hackathonId}/admins`);
    return response.data;
  }

  async addCommunityAdmin(hackathonId: string, userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/community/${hackathonId}/admins`, { userId });
    return response.data;
  }

  async removeCommunityAdmin(hackathonId: string, userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/community/${hackathonId}/admins/${userId}`);
    return response.data;
  }

  async removeUserFromCommunity(hackathonId: string, userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/community/${hackathonId}/users/${userId}`);
    return response.data;
  }

  async reAddUserToCommunity(hackathonId: string, userId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/community/${hackathonId}/users/${userId}/re-add`);
    return response.data;
  }

  async deleteDirectMessage(messageId: string): Promise<any> {
    return this.api.delete(`/api/users/messages/${messageId}`);
  }

  async getOnlineStatus(): Promise<ApiResponse<{ [userId: string]: boolean }>> {
    const response: AxiosResponse<ApiResponse<{ [userId: string]: boolean }>> = await this.api.get('/api/users/users/online-status');
    return response.data;
  }

  async getUserOnlineStatus(userId: string): Promise<ApiResponse<{ isOnline: boolean }>> {
    const response: AxiosResponse<ApiResponse<{ isOnline: boolean }>> = await this.api.get(`/api/users/${userId}/online-status`);
    return response.data;
  }

  async getUnreadMessageCounts(): Promise<ApiResponse<{ [userId: string]: number }>> {
    const response: AxiosResponse<ApiResponse<{ [userId: string]: number }>> = await this.api.get('/api/users/messages/unread-counts');
    return response.data;
  }

  // Social connection endpoints
  async getFollowers(userId: string): Promise<ApiResponse<User[]>> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get(`/api/users/${userId}/followers`);
    return response.data;
  }

  async getFollowing(userId: string): Promise<ApiResponse<User[]>> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get(`/api/users/${userId}/following`);
    return response.data;
  }

  async getConnections(userId: string): Promise<ApiResponse<User[]>> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get(`/api/users/${userId}/connections`);
    return response.data;
  }

  async removeConnection(userId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/users/connections/${userId}/remove`);
    return response.data;
  }

  // Blocking and reporting endpoints
  async blockUser(userId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/users/${userId}/block`);
    return response.data;
  }

  async unblockUser(userId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/users/${userId}/unblock`);
    return response.data;
  }

  async reportUser(userId: string, reason: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/users/${userId}/report`, { reason });
    return response.data;
  }

  async getBlockStatus(userId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/users/${userId}/block-status`);
    return response.data;
  }

  async getUserProfileByUsername(username: string): Promise<ApiResponse<User>> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.get(`/api/users/username/${username}`);
    return response.data;
  }

  async searchUsers(query: string, page: number = 1): Promise<ApiResponse<{ users: User[]; total: number }>> {
    const response: AxiosResponse<ApiResponse<{ users: User[]; total: number }>> = await this.api.get(`/api/users/search/by?q=${encodeURIComponent(query)}&page=${page}&limit=20`);
    return response.data;
  }

  // Team search and management methods
  // User profile and search methods
  // async getUserProfiles(): Promise<ApiResponse<User[]>> {
  //   const response: AxiosResponse<ApiResponse<User[]>> =
  //     await this.api.get('/api/users/profile');
  //   return response.data;
  // }

  // Fix the API service methods for user profile and search



  // 2. Update the searchUser method to match backend endpoint and accept filters

  // User search method with improved parameter handling
  // async searchUser(
  //   query: string = '', 
  //   page: number = 1, 
  //   filters?: UserFilters
  // ): Promise<ApiResponse<{ users: User[], total: number, page: number, totalPages: number }>> {
  //   const params = new URLSearchParams({
  //     page: page.toString(),
  //     limit: '20'
  //   });

  //   // Add search query if provided
  //   if (query && query.trim()) {
  //     params.append('search', query.trim());
  //   }

  //   // Add filters if provided
  //   if (filters) {
  //     if (filters.college && filters.college.trim()) {
  //       params.append('college', filters.college.trim());
  //     }
  //     if (filters.collegeYear && filters.collegeYear !== 'all') {
  //       params.append('collegeYear', filters.collegeYear);
  //     }
  //     if (filters.branch && filters.branch.trim()) {
  //       params.append('branch', filters.branch.trim());
  //     }
  //     if (filters.gender && filters.gender !== 'all') {
  //       params.append('gender', filters.gender);
  //     }
  //     if (filters.hackathonName && filters.hackathonName.trim()) {
  //       params.append('hackathon', filters.hackathonName.trim());
  //     }
  //     if (filters.skills && filters.skills.length > 0) {
  //       filters.skills.forEach(skill => params.append('skills', skill));
  //     }
  //   }

  //   console.log('User search URL:', `/api/users/search/team?${params.toString()}`);

  //   const response: AxiosResponse<ApiResponse<{ users: User[], total: number, page: number, totalPages: number }>> =
  //     await this.api.get(`/api/users/search/team?${params.toString()}`);
  //   return response.data;
  // }

  // Updated team search method with enhanced filtering
  async searchTeams(
    query: string = '',
    page: number = 1,
    filters: TeamFilters
  ): Promise<ApiResponse<{ teams: Team[]; total: number; page: number; totalPages: number; hasMore: boolean }>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });

      // Add search query if provided
      if (query && query.trim()) {
        params.append('search', query.trim());
      }

      // Add filter parameters only if they're not default values
      if (filters.collegeType !== 'all') {
        params.append('collegeType', filters.collegeType);
      }

      if (filters.location !== 'all') {
        params.append('location', filters.location);
      }

      if (filters.hackathonName && filters.hackathonName.trim()) {
        params.append('hackathonName', filters.hackathonName.trim());
      }

      // Add college name filter
      if (filters.collegeName && filters.collegeName.trim()) {
        params.append('collegeName', filters.collegeName.trim());
      }

      // Add array parameters
      if (filters.states && filters.states.length > 0) {
        filters.states.forEach(state => params.append('states', state));
      }

      if (filters.domains && filters.domains.length > 0) {
        filters.domains.forEach(domain => params.append('domains', domain));
      }

      console.log('Team search URL:', `/api/users/teams/search?${params.toString()}`);

      const response: AxiosResponse<ApiResponse<{
        teams: Team[];
        total: number;
        page: number;
        totalPages: number;
        hasMore: boolean;
      }>> = await this.api.get(`/api/users/teams/search?${params.toString()}`);

      return response.data;
    } catch (error) {
      console.error('API searchTeams error:', error);
      throw error;
    }
  }





  async createTeam(teamData: CreateTeamData): Promise<ApiResponse<Team>> {
    const response: AxiosResponse<ApiResponse<Team>> =
      await this.api.post('/api/teams', teamData);
    return response.data;
  }

  async getTeamDetail(teamId: string): Promise<ApiResponse<TeamDetail>> {
    const response: AxiosResponse<ApiResponse<TeamDetail>> =
      await this.api.get(`/api/users/teams/${teamId}`);
    return response.data;
  }

  async sendTeamJoinRequest(teamId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> =
      await this.api.post(`/api/users/teams/${teamId}/join-request`);
    return response.data;
  }

  async withdrawJoinRequest(teamId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> =
      await this.api.delete(`/api/users/teams/${teamId}/join-request`);
    return response.data;
  }

  async getMySentRequests(): Promise<ApiResponse<SentJoinRequest[]>> {
    const response: AxiosResponse<ApiResponse<SentJoinRequest[]>> =
      await this.api.get('/api/users/teams/my-requests/sent');
    return response.data;
  }

  async getMyTeams(): Promise<ApiResponse<MyTeam[]>> {
    const response: AxiosResponse<ApiResponse<MyTeam[]>> =
      await this.api.get('/api/users/teams/my-teams');
    return response.data;
  }

  async acceptJoinRequest(requestId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> =
      await this.api.post(`/api/users/teams/requests/${requestId}/accept`);
    return response.data;
  }

  async rejectJoinRequest(requestId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> =
      await this.api.post(`/api/users/teams/requests/${requestId}/reject`);
    return response.data;
  }

  async removeTeamMember(teamId: string, userId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> =
      await this.api.delete(`/api/users/teams/${teamId}/members/${userId}`);
    return response.data;
  }

  async deleteTeam(teamId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> =
      await this.api.delete(`/api/users/teams/${teamId}`);
    return response.data;
  }



  // Add to your apiService
  // Contact form methods
  async submitContactForm(formData: FormData): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post('/api/contact/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error: any) {
      console.error('Contact form submission error:', error);
      throw error;
    }
  }

  async uploadContactImages(files: File[]): Promise<ApiResponse<string[]>> {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('images', file));

      const response = await this.api.post('/api/contact/upload-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error: any) {
      console.error('Contact images upload error:', error);
      throw error;
    }
  }

  // =====================================================
  // MULTI-STEP REGISTRATION & APPROVAL SYSTEM APIs
  // =====================================================

  // Registration Configuration APIs
  async getEventRegistrationConfig(eventId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/events/${eventId}/registration-config`);
    return response.data;
  }

  async updateEventRegistrationConfig(eventId: string, config: {
    applicationType?: 'free_limited' | 'free_unlimited' | 'paid_limited' | 'paid_unlimited';
    userLimit?: number;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/events/${eventId}/registration-config`, config);
    return response.data;
  }

  // Custom Questions APIs
  async getEventCustomQuestions(eventId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/events/${eventId}/custom-questions`);
    return response.data;
  }

  async addEventCustomQuestion(eventId: string, question: {
    questionText: string;
    questionType: 'short_text' | 'long_text' | 'radio' | 'checkbox' | 'dropdown' | 'yes_no' | 'number' | 'file_upload';
    options?: string[];
    required?: boolean;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/events/${eventId}/custom-questions`, question);
    return response.data;
  }

  async updateEventCustomQuestion(eventId: string, questionId: string, question: {
    questionText?: string;
    questionType?: string;
    options?: string[];
    required?: boolean;
    orderIndex?: number;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/events/${eventId}/custom-questions/${questionId}`, question);
    return response.data;
  }

  async deleteEventCustomQuestion(eventId: string, questionId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/events/${eventId}/custom-questions/${questionId}`);
    return response.data;
  }

  async reorderEventCustomQuestions(eventId: string, questionOrder: Array<{ questionId: string; orderIndex: number }>): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/events/${eventId}/custom-questions/reorder`, { questionOrder });
    return response.data;
  }

  // Application Flow APIs (User-side)
  async submitEventBasicInfo(eventId: string, basicInfo: {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    college: string;
    course: string;
    branch: string;
    collegeYear: string;
    country: string;
    state: string;
    referralCode?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/events/${eventId}/apply/basic`, basicInfo);
    return response.data;
  }

  async submitEventAdditionalAnswers(eventId: string, answers: Array<{
    questionId: string;
    answer: any;
    fileUrl?: string;
  }>): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/events/${eventId}/apply/additional`, { answers });
    return response.data;
  }

  async getMyEventApplication(eventId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/events/${eventId}/my-application`);
    return response.data;
  }

  async uploadEventAnswerFile(eventId: string, file: File): Promise<ApiResponse<{ fileUrl: string; fileName: string }>> {
    const formData = new FormData();
    formData.append('file', file);

    const response: AxiosResponse<ApiResponse<{ fileUrl: string; fileName: string }>> = await this.api.post(
      `/api/events/${eventId}/upload-answer-file`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  // Application Payment APIs
  async createEventApplicationPaymentOrder(eventId: string): Promise<ApiResponse<{
    orderId: string;
    amount: number;
    currency: string;
    key: string;
  }>> {
    const response: AxiosResponse<ApiResponse<{ orderId: string; amount: number; currency: string; key: string }>> =
      await this.api.post(`/api/events/${eventId}/application-payment/create-order`);
    return response.data;
  }

  async verifyEventApplicationPayment(eventId: string, data: {
    orderId: string;
    paymentId: string;
    signature: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/events/${eventId}/application-payment/verify`, data);
    return response.data;
  }

  // Host Dashboard APIs (Review/Approval)
  async getEventApplications(eventId: string, status?: string): Promise<ApiResponse<any>> {
    const params = status ? { status } : {};
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/events/${eventId}/applications`, { params });
    return response.data;
  }

  async getEventApplicationDetail(eventId: string, participantId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/events/${eventId}/application/${participantId}`);
    return response.data;
  }

  async reviewEventApplication(eventId: string, participantId: string, data: {
    action: 'approve' | 'reject';
    notes?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/events/${eventId}/applications/${participantId}/review`, data);
    return response.data;
  }

  async reviewEventApplicationsBulk(eventId: string, data: {
    participantIds: string[];
    action: 'approve' | 'reject';
    notes?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/events/${eventId}/applications/review-bulk`, data);
    return response.data;
  }

  // Event Admin Management
  async getEventAdmins(eventId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/events/${eventId}/admins`);
    return response.data;
  }

  async addEventAdmin(eventId: string, data: { email: string; role: 'editor' | 'moderator' }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/events/${eventId}/admins`, data);
    return response.data;
  }

  async removeEventAdmin(eventId: string, userId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/events/${eventId}/admins/${userId}`);
    return response.data;
  }

  // Hackathon Custom Questions APIs
  async getHackathonCustomQuestions(hackathonId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/hackathons/${hackathonId}/custom-questions`);
    return response.data;
  }

  async addHackathonCustomQuestion(hackathonId: string, question: {
    questionText: string;
    questionType: 'short_text' | 'long_text' | 'radio' | 'checkbox' | 'dropdown' | 'yes_no' | 'number' | 'file_upload';
    options?: string[];
    required?: boolean;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${hackathonId}/custom-questions`, question);
    return response.data;
  }

  async updateHackathonCustomQuestion(hackathonId: string, questionId: string, question: {
    questionText?: string;
    questionType?: string;
    options?: string[];
    required?: boolean;
    orderIndex?: number;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/hackathons/${hackathonId}/custom-questions/${questionId}`, question);
    return response.data;
  }

  async deleteHackathonCustomQuestion(hackathonId: string, questionId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/api/hackathons/${hackathonId}/custom-questions/${questionId}`);
    return response.data;
  }

  async reorderHackathonCustomQuestions(hackathonId: string, questionOrder: Array<{ questionId: string; orderIndex: number }>): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/hackathons/${hackathonId}/custom-questions/reorder`, { questionOrder });
    return response.data;
  }

  // Hackathon Application Flow APIs (User-side)
  async submitHackathonBasicInfo(hackathonId: string, basicInfo: {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    college: string;
    course: string;
    branch: string;
    collegeYear: string;
    country: string;
    state: string;
    referralCode?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${hackathonId}/apply/basic`, basicInfo);
    return response.data;
  }

  async submitHackathonAdditionalAnswers(hackathonId: string, answers: Array<{
    questionId: string;
    answer: any;
    fileUrl?: string;
  }>): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/api/hackathons/${hackathonId}/apply/additional`, { answers });
    return response.data;
  }

  async getMyHackathonApplication(hackathonId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/hackathons/${hackathonId}/my-application`);
    return response.data;
  }

  async uploadHackathonAnswerFile(hackathonId: string, file: File): Promise<ApiResponse<{ fileUrl: string; fileName: string }>> {
    const formData = new FormData();
    formData.append('file', file);

    const response: AxiosResponse<ApiResponse<{ fileUrl: string; fileName: string }>> = await this.api.post(
      `/api/hackathons/${hackathonId}/upload-answer-file`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  // Team Discovery/Search endpoints
  async discoverTeams(params: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    location?: string;
    remoteOK?: boolean;
    rolesNeeded?: string;
    sortBy?: string;
    states?: string;
  }): Promise<any> {
    // Remove undefined/empty params
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== undefined && v !== '' && v !== false)
    );

    const response = await this.api.get('/api/teams/discover', { params: filteredParams });
    return response.data;
  }

  async getTeamDetails(hackathonId: string, teamId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/hackathons/${hackathonId}/teams/${teamId}`);
    return response.data;
  }

  async toggleTeamVisibility(teamId: string, visibleInSearch: boolean): Promise<ApiResponse<{ visibleInSearch: boolean }>> {
    const response: AxiosResponse<ApiResponse<{ visibleInSearch: boolean }>> = await this.api.patch(
      `/api/teams/${teamId}/visibility`,
      { visibleInSearch }
    );
    return response.data;
  }

  async updateTeam(teamId: string, teamData: Partial<CreateTeamData>): Promise<ApiResponse<Team>> {
    const response: AxiosResponse<ApiResponse<Team>> = await this.api.patch(
      `/api/teams/${teamId}`,
      teamData
    );
    return response.data;
  }
  // Certificate endpoints
  async getUserCertificates(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/api/users/certificates');
    return response.data;
  }

  async updateHackathonCertificateConfig(id: string, config: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/hackathons/${id}/certificate-config`, config);
    return response.data;
  }

  async getHackathonCertificateConfig(id: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/hackathons/${id}/certificate-config`);
    return response.data;
  }

  async updateEventCertificateConfig(id: string, config: any): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/api/events/${id}/certificate-config`, config);
    return response.data;
  }

  async getEventCertificateConfig(id: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/events/${id}/certificate-config`);
    return response.data;
  }
  // Certificate
  async generateCertificateId(payload: { entityType: 'event' | 'hackathon'; entityId: string }): Promise<{ success: boolean; certificateId: string; message?: string }> {
    const response = await this.api.post('/api/certificates/generate', payload);
    return response.data;
  }
  async uploadEventImage(file: File): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('image', file);

    const response: AxiosResponse<ApiResponse<{ url: string }>> = await this.api.post(
      '/api/events/upload-image',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  // Jobs API methods
  async getJobs(params?: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/jobs', { params });
    return response.data;
  }

  async getJob(id: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/api/jobs/${id}`);
    return response.data;
  }

  // Referral methods
  async recordSignup(data: {
    referredUserId: string;
    referralCode: string;
    deviceId?: string;
    phone?: string;
  }): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/referral/record-signup', data);
    return response.data;
  }

  async getMyReferrals(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/api/referral/my');
    return response.data;
  }

  async recordActivity(activityType: 'hackathon' | 'quiz' | 'job'): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/api/referral/record-activity', { activityType });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;